import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import {
    Banner,
    Button,
    Form,
    FormLayout,
    Frame,
    Layout,
    Loading,
    Page,
    TextField,
} from "@shopify/polaris";
import { useStore } from "../../app/stores/store";
import { UserFormValues } from "../../app/models/User";
import { getShopifyAuthFromSearch, stripShopifyAuthParams } from "../../app/utils/shopifyAuth";

declare global {
    interface Window {
        shopify?: {
            idToken?: () => Promise<string>;
            toast?: { show?: (msg: string | { message: string; duration?: number }) => void };
        };
    }
}

const LoginPage = () => {
    const { userStore } = useStore();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [storeDomain, setStoreDomain] = useState("");
    const [authParamsPresent, setAuthParamsPresent] = useState(false);
    const [authError, setAuthError] = useState("");

    useEffect(() => {
        const authParams = getShopifyAuthFromSearch(`?${searchParams.toString()}`);
        const hasAuthorizationCode = Boolean(authParams.shop && authParams.code && authParams.state);
        const hasEmbeddedIdToken = Boolean(authParams.shop && authParams.embedded && authParams.id_token);
        const isManagedEmbeddedLaunch = Boolean(authParams.shop && (authParams.embedded || authParams.host));

        if (hasAuthorizationCode || hasEmbeddedIdToken || isManagedEmbeddedLaunch) {
            setAuthParamsPresent(true);

            const authenticateUser = async () => {
                try {
                    if (authParams.code && authParams.state) {
                        //console.log("Performing regular oAuth login.");
                        await userStore.login(authParams);
                    } else if (authParams.shop && (authParams.embedded || authParams.host)) {
                        let idToken = authParams.id_token;

                        if (!idToken && window.shopify?.idToken) {
                            // Shopify managed install may launch the embedded app without
                            // id_token in the URL. App Bridge can mint one for token exchange.
                            idToken = await window.shopify.idToken();
                        }

                        if (!idToken) {
                            throw new Error("Shopify did not provide an embedded app ID token.");
                        }

                        await userStore.embeddedLogin({
                            ...authParams,
                            embedded: authParams.embedded || "1",
                            id_token: idToken,
                        });
                    } else {
                        throw new Error("Missing Shopify embedded app launch parameters.");
                    }
                } catch (error) {
                    setAuthError("Unable to authenticate the embedded Shopify app session.");
                    setAuthParamsPresent(false);
                }
            };

            authenticateUser();
        }
    }, [searchParams, userStore]);

    useEffect(() => {
        if (!userStore.isLoggedIn) {
            return;
        }

        const stateFrom = (location.state as { from?: Location } | null)?.from;
        const currentRouteIsLogin = location.pathname === "/login" || location.pathname === "/";
        const fromPath = stateFrom?.pathname || (currentRouteIsLogin ? "/dashboard" : location.pathname);
        const rawFromSearch = stateFrom?.search || (currentRouteIsLogin ? "" : location.search);
        const fromSearch = stripShopifyAuthParams(rawFromSearch);
        navigate(`${fromPath}${fromSearch}`, { replace: true });
    }, [location.pathname, location.search, location.state, navigate, userStore.isLoggedIn]);

    const handleSubmit = () => {
        userStore.authenticate({ shopDomain: storeDomain } as UserFormValues);
    };

    const handleShopDomainChange = useCallback((value: string) => {
        setStoreDomain(value);
    }, []);

    if (authParamsPresent) {
        return (
            <div style={{ height: "100px" }}>
                <Frame>
                    <Loading />
                </Frame>
            </div>
        );
    }

    if (authError) {
        return (
            <Page title="Login">
                <Banner tone="critical" title="Shopify app session could not be started">
                    <p>{authError}</p>
                </Banner>
            </Page>
        );
    }

    return (
        <Page title="Login">
            <Layout>
            </Layout>
        </Page>
    );
};

export default observer(LoginPage);
